import { useEffect, useState } from "react";

function formatClock(d: Date) {
  const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const months = [
    "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
    "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
  ];
  const day = days[d.getDay()];
  const month = months[d.getMonth()];
  const date = String(d.getDate()).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const mins = String(d.getMinutes()).padStart(2, "0");
  return {
    date: `${day} · ${date} ${month} ${year}`,
    time: `${hours}:${mins} PST`,
  };
}

interface CockpitHeaderProps {
  systemOnline?: boolean;
  weather?: { tempF: number; location: string; icon: string };
  /** Open the Pipeline kanban modal. Omitted in focus mode if not wired. */
  onOpenPipeline?: () => void;
  /** Open the macro Tasks modal — paired with Pipeline. */
  onOpenTasks?: () => void;
  /** Open the Calendar modal (Phase C.1). */
  onOpenCalendar?: () => void;
  /** Open the standalone Email workspace (Piece 2). */
  onOpenEmail?: () => void;
  /** Open the New Intake modal (Surface A). Cockpit only. */
  onNewIntake?: () => void;
}

export function CockpitHeader({
  systemOnline = true,
  weather = { tempF: 66, location: "WOODLAND HILLS", icon: "⛅" },
  onOpenPipeline,
  onOpenTasks,
  onOpenCalendar,
  onOpenEmail,
  onNewIntake,
}: CockpitHeaderProps) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);
  const clock = formatClock(now);

  return (
    <div className="header">
      <div className="brand-block">
        <div className="brand">EDGE</div>
        <div className="brand-sub">WESTEDGE COMMAND DECK · v0.1</div>
      </div>

      <div className="header-spacer" />

      <div className="system-indicator">
        <div className="system-dot" />
        <div>
          <div className="system-indicator-label">SYSTEMS</div>
          <div className="system-indicator-status">
            {systemOnline ? "ONLINE" : "DEGRADED"}
          </div>
        </div>
      </div>

      {onNewIntake && (
        <button
          className="header-icon-btn new-intake-btn"
          onClick={onNewIntake}
          title="New Intake — capture a new deal"
          aria-label="New Intake"
        >
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
            <rect
              x="2.5" y="1.5" width="9" height="13" rx="1"
              fill="none" stroke="currentColor" strokeWidth="1.2"
            />
            <line x1="8" y1="9.5" x2="8" y2="5.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            <line x1="6" y1="7.5" x2="10" y2="7.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        </button>
      )}

      {onOpenPipeline && (
        <button
          className="header-icon-btn pipeline-btn"
          onClick={onOpenPipeline}
          title="Open Pipeline view"
          aria-label="Open Pipeline view"
        >
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
            <rect x="1" y="2" width="3" height="12" fill="currentColor" />
            <rect x="6.5" y="2" width="3" height="8" fill="currentColor" />
            <rect x="12" y="2" width="3" height="10" fill="currentColor" />
          </svg>
        </button>
      )}

      {onOpenTasks && (
        <button
          className="header-icon-btn tasks-btn"
          onClick={onOpenTasks}
          title="Open Tasks view"
          aria-label="Open Tasks view"
        >
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
            <rect
              x="2.5"
              y="1.5"
              width="11"
              height="13"
              rx="1"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.2"
            />
            <rect x="5" y="0.5" width="6" height="2.5" rx="0.5" fill="currentColor" />
            <path
              d="M5 8.5 L7 10.5 L11 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      {onOpenCalendar && (
        <button
          className="header-icon-btn calendar-btn"
          onClick={onOpenCalendar}
          title="Open Calendar"
          aria-label="Open Calendar"
        >
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
            <rect
              x="1.5"
              y="3"
              width="13"
              height="11"
              rx="1"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.2"
            />
            <line
              x1="1.5"
              y1="6"
              x2="14.5"
              y2="6"
              stroke="currentColor"
              strokeWidth="1.2"
            />
            <line x1="4.5" y1="1.5" x2="4.5" y2="4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            <line x1="11.5" y1="1.5" x2="11.5" y2="4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
        </button>
      )}

      {onOpenEmail && (
        <button
          className="header-icon-btn email-btn"
          onClick={onOpenEmail}
          title="Open Email"
          aria-label="Open Email"
        >
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
            <rect
              x="1.5"
              y="3"
              width="13"
              height="10"
              rx="1"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.2"
            />
            <path
              d="M2 4 L8 8.5 L14 4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      <div className="weather-block">
        <div className="weather-icon">{weather.icon}</div>
        <div>
          <div className="weather-temp">{weather.tempF}°F</div>
          <div className="weather-loc">{weather.location}</div>
        </div>
      </div>

      <div className="clock-block">
        <div className="clock-date">{clock.date}</div>
        <div className="clock-time">{clock.time}</div>
      </div>
    </div>
  );
}
